import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Check, ExternalLink, Zap, Building2, Code, Chrome, AlertTriangle, ShieldCheck, Clock, Database } from "lucide-react";
import LastUpdated from "@/components/LastUpdated";
import PricingCalculator from "@/components/PricingCalculator";
import { trackEvent, EXTENSION_URL } from "@/lib/analytics";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";
import { COMPETITOR_PRICING, COMPETITOR_ORDER } from "@/lib/competitor-pricing";


const FAQ_QA: { q: string; a: string }[] = [
  {
    q: "How much does the DeepSeek API cost?",
    a: "Off-peak, DeepSeek-V4.1-Flash is $0.15 per 1M input tokens (cache miss), $0.003 cache hit, and $0.60 per 1M output tokens. DeepSeek-V4-Pro is $0.66 / $0.022 / $1.98 per 1M tokens. During peak hours (01:00-04:00 and 06:00-10:00 UTC, Monday to Friday) every rate doubles: Flash $0.30 / $0.006 / $1.20 and Pro $1.32 / $0.044 / $3.96. All rates are per 1 million tokens in USD, independently verified 18 September 2026.",
  },
  {
    q: "What is cache hit vs cache miss pricing?",
    a: "DeepSeek automatically caches the prefix of your prompts on disk. Repeated input tokens (a system prompt, a long document, few-shot examples) are billed at the much lower cache-hit rate instead of the cache-miss rate. No SDK changes or headers required — DeepSeek shipped disk-based context caching on 2 August 2024 and it has applied automatically ever since.",
  },
  {
    q: "How much can context caching save me?",
    a: "For workloads with a stable prefix, cache hits drop your effective input price by ~80–95%. If 90% of input tokens on V4.1-Flash are cache hits, your effective off-peak input rate is roughly $0.018 per 1M instead of $0.15 — an ~88% saving with no code changes.",
  },
  {
    q: "Did DeepSeek prices go up or down in 2026?",
    a: "Both directions. On 31 May 2026 the V4-Pro standard rates were cut from $1.74 / $0.0145 / $3.48 to $0.435 / $0.003625 / $0.87 per 1M tokens (input miss / hit / output). Alongside the DeepSeek-V4.1-Flash general-availability release on 9 September 2026 the rate card changed again: it now has two time tiers, and both the off-peak and the peak numbers sit above the old flat rates. V4-Pro is $0.66 / $0.022 / $1.98 off-peak and double that during peak hours.",
  },
  {
    q: "Does the DeepSeek API have peak and off-peak pricing?",
    a: "Yes. The official rate card now lists two tiers. Peak hours are 01:00-04:00 and 06:00-10:00 UTC, Monday to Friday (Beijing 09:00-12:00 and 14:00-18:00); every other hour, including weekends, is off-peak. Off-peak rates are exactly half of peak rates, so peak is a 100% surcharge. The separate historical V3/R1 off-peak discount — 50% off DeepSeek-V3 and 75% off DeepSeek-R1 during 16:30-00:30 UTC — ended when the deepseek-chat and deepseek-reasoner aliases were retired on 24 July 2026 at 15:59 UTC.",
  },
  {
    q: "Is DeepSeek free to use?",
    a: "The DeepSeek web and mobile apps are free to chat with. The API is paid, per the token rates listed. There is no permanent free API tier, though promotional credits appear from time to time.",
  },
  {
    q: "Why is DeepSeek so much cheaper than ChatGPT or Claude?",
    a: "Efficient mixture-of-experts architecture with a small active-parameter count, aggressive automatic prefix caching, and Chinese-market-optimised training costs let DeepSeek price frequently 10–30× cheaper than comparable US frontier APIs for similar tasks.",
  },
  {
    q: "What happened to deepseek-chat and deepseek-reasoner?",
    a: "The legacy model names deepseek-chat and deepseek-reasoner were retired on 24 July 2026 at 15:59 UTC. Before that date they mapped to V4-Flash in non-thinking and thinking modes respectively; since then they are fully retired and inaccessible. Move all code to deepseek-flash (which serves DeepSeek-V4.1-Flash) or deepseek-v4-pro. The names deepseek-v4-flash and deepseek-v4-flash-vision-exp are still accepted, but those models have been retired and their requests are served by V4.1-Flash at the Flash price.",
  },
  {
    q: "V4.1-Flash or V4-Pro — which should I use?",
    a: "V4.1-Flash is the default workhorse: cheap, fast, 1M-token context, ideal for chat, extraction, classification and high-volume tasks. V4-Pro is the frontier tier for the hardest reasoning, agentic and coding work. Start on Flash, route only the hardest calls to Pro.",
  },
  {
    q: "How does DeepSeek billing work?",
    a: "DeepSeek bills on tokens, not requests or seats. You pay for input tokens (your prompt) plus output tokens (the completion, including reasoning tokens in thinking mode). No monthly minimum, no per-seat fee — deducted post-paid from your prepaid balance.",
  },
  {
    q: "What are the DeepSeek API rate limits?",
    a: "DeepSeek documents concurrency limits only — no requests-per-minute and no tokens-per-minute figures are published. The documented ceilings are 500 concurrent requests for deepseek-v4-pro and 2,500 for deepseek-v4-flash. There are no automatic account tiers: raising those ceilings requires an explicit capacity expansion request to DeepSeek. On 429 errors, cap your in-flight requests and retry with exponential back-off.",
  },
];


const Pricing = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_QA.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const flashRates = DEEPSEEK_PRICING.models["v4-flash"];
  const proRates = DEEPSEEK_PRICING.models["v4-pro"];

  const offerCatalogSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "DeepSeek API",
    description: "Per-token access to DeepSeek-V4-Flash and DeepSeek-V4-Pro.",
    brand: { "@type": "Brand", name: "DeepSeek" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: flashRates.inputCacheHit,
      highPrice: proRates.output,
      offerCount: 6,
      offers: [
        { "@type": "Offer", name: "V4-Flash input (cache miss) per 1M tokens", price: flashRates.inputCacheMiss, priceCurrency: "USD" },
        { "@type": "Offer", name: "V4-Flash input (cache hit) per 1M tokens", price: flashRates.inputCacheHit, priceCurrency: "USD" },
        { "@type": "Offer", name: "V4-Flash output per 1M tokens", price: flashRates.output, priceCurrency: "USD" },
        { "@type": "Offer", name: "V4-Pro input (cache miss) per 1M tokens", price: proRates.inputCacheMiss, priceCurrency: "USD" },
        { "@type": "Offer", name: "V4-Pro input (cache hit) per 1M tokens", price: proRates.inputCacheHit, priceCurrency: "USD" },
        { "@type": "Offer", name: "V4-Pro output per 1M tokens", price: proRates.output, priceCurrency: "USD" },
      ],
    },
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "DeepSeek Pricing 2026: V4-Flash & V4-Pro API Costs",
    description:
      "Full DeepSeek V4 API pricing for 2026: V4-Flash vs V4-Pro per-token costs, automatic cache-hit savings, price history and a free cost calculator. Independently verified.",
    url: "https://deepseek.ai/pricing",
    dateModified: DEEPSEEK_PRICING.lastVerified,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai" },
      { "@type": "ListItem", position: 2, name: "Pricing", item: "https://deepseek.ai/pricing" },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>DeepSeek API Pricing 2026: V4-Flash &amp; V4-Pro Per-Token Costs</title>
        <meta
          name="description"
          content="V4.1-Flash $0.15/M off-peak, V4-Pro $0.66/M, cache hits from $0.003, peak hours double. Free calculator, price history and full rate table. Verified 18 September."
        />
        <link rel="canonical" href="https://deepseek.ai/pricing" />
        <meta property="og:title" content="DeepSeek API Pricing 2026: V4-Flash & V4-Pro Per-Token Costs" />
        <meta
          property="og:description"
          content="Independently verified DeepSeek V4 pricing: per-token costs, cache-hit savings, price history and a free calculator."
        />
        <meta property="og:url" content="https://deepseek.ai/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek API Pricing 2026: V4-Flash & V4-Pro Per-Token Costs" />
        <meta
          name="twitter:description"
          content="Per-token DeepSeek V4 pricing with cache-hit savings, price history and a free calculator. Independently verified."
        />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(offerCatalogSchema)}</script>
      </Head>

      <main className="container mx-auto px-4 py-8">
        <Breadcrumbs currentPage="Pricing" />

        {/* Hero */}
        <section className="text-center mb-10 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              DeepSeek Pricing 2026: V4-Flash &amp; V4-Pro API Costs
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            DeepSeek is one of the cheapest frontier-model APIs available in 2026 — often <strong>10–30× cheaper</strong> than comparable models from OpenAI or Anthropic. This page breaks down exactly what you pay: the two current models (<strong>V4.1-Flash</strong> and <strong>V4-Pro</strong>), how <strong>cache hit vs cache miss</strong> input pricing works, the live <strong>peak vs off-peak</strong> tiers, and a <strong>calculator</strong> to estimate your own bill.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            All prices are per <strong>1 million tokens</strong>, in <strong>USD</strong>, and independently verified against DeepSeek's official pricing page.
          </p>
          <div className="flex justify-center">
            <LastUpdated date={DEEPSEEK_PRICING.lastVerified} />
          </div>
        </section>

        {/* Verification banner */}
        <section className="mb-12 max-w-4xl mx-auto">
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold mb-2 text-foreground">Independent guide — always confirm live pricing on the official page before budgeting.</p>
                <p className="text-muted-foreground">
                  deepseek.ai is an independent editorial resource and is not affiliated with DeepSeek. The rate mechanisms below are correct, but DeepSeek adjusts figures frequently. Cross-check the {" "}
                  <a href={DEEPSEEK_PRICING.source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    official DeepSeek API pricing page
                  </a>{" "}
                  before committing to a budget.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing at a glance */}
        <section className="mb-16 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">DeepSeek API pricing at a glance</h2>
          <p className="text-muted-foreground mb-6">
            The API exposes two models. <strong>DeepSeek-V4.1-Flash</strong> went generally available on <strong>9 September 2026</strong> and is served by the model name <code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-flash</code>; the older <code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-v4-flash</code> and <code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-v4-flash-vision-exp</code> names are still accepted but those models are retired and their requests are served by V4.1-Flash at the Flash price. <strong>DeepSeek-V4-Pro</strong> (<code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-v4-pro</code>) continues past 14 September 2026 with unchanged billing. The legacy names <code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-chat</code> and <code className="text-sm bg-muted px-1.5 py-0.5 rounded">deepseek-reasoner</code> were <strong>retired on 24 July 2026 at 15:59 UTC</strong> and are fully inaccessible — see{" "}
            <Link to="/blog/deepseek-chat-reasoner-retired-billing-impact" className="text-primary hover:underline font-medium">
              deepseek-reasoner retired: migrate to V4.1-Flash, not Pro
            </Link>.
          </p>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Table 1 — Current DeepSeek API rates per 1M tokens, USD, off-peak and peak</caption>
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Model</th>
                  <th className="text-left py-3 px-4 font-semibold">Tier</th>
                  <th className="text-right py-3 px-4 font-semibold">Input · cache miss</th>
                  <th className="text-right py-3 px-4 font-semibold">Input · cache hit</th>
                  <th className="text-right py-3 px-4 font-semibold">Output</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4.1-Flash</td>
                  <td className="py-3 px-4">Off-peak</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${flashRates.inputCacheMiss.toFixed(2)}</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${flashRates.inputCacheHit.toFixed(3)}</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${flashRates.output.toFixed(2)}</td>
                </tr>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4.1-Flash</td>
                  <td className="py-3 px-4">Peak</td>
                  <td className="text-right py-3 px-4">${flashRates.peak.inputCacheMiss.toFixed(2)}</td>
                  <td className="text-right py-3 px-4">${flashRates.peak.inputCacheHit.toFixed(3)}</td>
                  <td className="text-right py-3 px-4">${flashRates.peak.output.toFixed(2)}</td>
                </tr>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4-Pro</td>
                  <td className="py-3 px-4">Off-peak</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${proRates.inputCacheMiss.toFixed(2)}</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${proRates.inputCacheHit.toFixed(3)}</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">${proRates.output.toFixed(2)}</td>
                </tr>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4-Pro</td>
                  <td className="py-3 px-4">Peak</td>
                  <td className="text-right py-3 px-4">${proRates.peak.inputCacheMiss.toFixed(2)}</td>
                  <td className="text-right py-3 px-4">${proRates.peak.inputCacheHit.toFixed(3)}</td>
                  <td className="text-right py-3 px-4">${proRates.peak.output.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Table 1 — Current DeepSeek API rates, per 1M tokens, USD. Peak hours are 01:00–04:00 and 06:00–10:00 UTC, Monday to Friday; all other hours are off-peak. Verified {DEEPSEEK_PRICING.lastVerified}.
          </p>

          {/* Price history */}
          <div className="mt-6 rounded-lg border bg-muted/30 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Price history</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Older DeepSeek pricing tables and long-lived blog posts sometimes still quote the pre-cut rates. For reference:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium">Model</th>
                    <th className="text-left py-2 px-3 font-medium">Effective until</th>
                    <th className="text-right py-2 px-3 font-medium">Input miss</th>
                    <th className="text-right py-2 px-3 font-medium">Input hit</th>
                    <th className="text-right py-2 px-3 font-medium">Output</th>
                  </tr>
                </thead>
                <tbody>
                  {DEEPSEEK_PRICING.history.map((h) => (
                    <tr key={`${h.model}-${h.effectiveUntil}`} className="border-t">
                      <td className="py-2 px-3">{DEEPSEEK_PRICING.models[h.model].label}</td>
                      <td className="py-2 px-3">{h.effectiveUntil}</td>
                      <td className="text-right py-2 px-3">${h.rates.inputCacheMiss.toFixed(3)}</td>
                      <td className="text-right py-2 px-3">${h.rates.inputCacheHit.toFixed(4)}</td>
                      <td className="text-right py-2 px-3">${h.rates.output.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              The V4-Pro cut was introduced as a 75% promotional discount, then made permanent — see the{" "}
              <Link to="/blog/deepseek-v4-pro-api-price-cut-permanent" className="text-primary hover:underline">
                permanent V4-Pro price-cut post
              </Link>{" "}
              for the full context.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" /> V4.1-Flash
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                The default workhorse: cheap, fast, 1M-token context. Great for chat, extraction, classification and high-volume production tasks. Start every new integration here.
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Code className="h-5 w-5 text-primary" /> V4-Pro
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Frontier tier for the hardest reasoning, agentic and coding work. At $0.66 / $1.98 per 1M tokens off-peak (double during peak hours), Pro costs roughly 3.3× Flash — route only your hardest calls here to keep bills lean.
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Calculator */}
        <section className="mb-16 max-w-4xl mx-auto">
          <PricingCalculator />
          <p className="text-sm text-muted-foreground text-center mt-4">
            Need to wire this up in code? See the <Link to="/deepseek-api" className="text-primary hover:underline">DeepSeek API setup guide</Link>.
          </p>
        </section>

        {/* Cache */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Database className="h-7 w-7 text-primary" />
            How context caching cuts your bill
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            DeepSeek automatically caches the <strong>prefix</strong> of your prompts on disk. When a new request repeats a prefix it has recently seen — a system prompt, a long document, a few-shot example set — those repeated input tokens are billed at the much lower <strong>cache-hit</strong> rate instead of the <strong>cache-miss</strong> rate. This is automatic: <strong>no SDK changes, no cache-control headers, no opt-in flag</strong>. DeepSeek shipped disk-based context caching on <strong>2 August 2024</strong>, and it has applied to every model generation since — including V4.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            The practical impact is large for any workload with a stable prefix. If 90% of your input tokens are cache hits on V4.1-Flash, your effective off-peak input price drops from $0.15 to roughly <strong>$0.018 per 1M</strong> — an ~88% saving on input, with no code changes. To maximise it: keep the reusable part of your prompt (system instructions, context documents) <strong>at the front</strong>, and the variable part at the end.
          </p>
        </section>

        {/* Peak-hour surcharge */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Clock className="h-7 w-7 text-primary" />
            Peak-hour pricing: now live on the official rate card
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            DeepSeek's old off-peak <em>discount</em> — 16:30–00:30 UTC daily, <strong>50% off DeepSeek-V3</strong> and <strong>75% off DeepSeek-R1</strong> — applied to the V3/R1 generation, <strong>not to V4</strong>. It ended with those models: the <code className="text-xs bg-muted px-1 rounded">deepseek-chat</code> and <code className="text-xs bg-muted px-1 rounded">deepseek-reasoner</code> aliases were retired on <strong>24 July 2026 at 15:59 UTC</strong>.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            What replaced it is the opposite. Since the <strong>DeepSeek-V4.1-Flash</strong> general-availability release, the official rate card carries <strong>two tiers</strong>: off-peak rates are exactly <strong>half</strong> of peak rates, so peak hours are a <strong>100% surcharge</strong>. Peak applies <strong>Monday to Friday only</strong>, in these two windows:
          </p>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Table 2 — Peak-hour windows on the official DeepSeek rate card</caption>
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4">Window</th>
                  <th className="text-left py-3 px-4">UTC</th>
                  <th className="text-left py-3 px-4">UTC+8 (Beijing)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">Morning</td>
                  <td className="py-3 px-4">01:00–04:00</td>
                  <td className="py-3 px-4">09:00–12:00</td>
                </tr>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium">Afternoon</td>
                  <td className="py-3 px-4">06:00–10:00</td>
                  <td className="py-3 px-4">14:00–18:00</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground leading-relaxed mt-4">
            Every other hour — including all day Saturday and Sunday — is off-peak, and the off-peak rows in Table 1 apply. That is 35 peak hours out of a 168-hour week, so a workload spread evenly across the whole week pays roughly <strong>21% more</strong> than the off-peak rate; one that avoids both windows pays <strong>nothing extra</strong>.
          </p>

          <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-4 flex items-start gap-3">
            <ShieldCheck className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Status as of 18 September 2026: active.</strong> DeepSeek's{" "}
              <a href={DEEPSEEK_PRICING.source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">official pricing page</a>{" "}
              now lists off-peak and peak columns for both models, with the windows above stated in UTC by DeepSeek itself. Earlier versions of this page reported the surcharge as announced-but-not-active, which was correct at the time; it went live alongside the V4.1-Flash GA release. An automated watcher checks the official rate card weekly and flags any change; the rates in Table 1 were last confirmed by a human editor on {DEEPSEEK_PRICING.lastVerified}.
            </p>
          </div>

        </section>

        {/* Worked examples */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Worked examples</h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground">Example A — a chatbot on V4.1-Flash.</strong> Off-peak, 1M input tokens (all cache miss) + 1M output tokens = <strong>$0.15 + $0.60 = $0.75</strong>. Add a stable system prompt so 90% of input is a cache hit and the same volume costs about <strong>$0.62</strong>. Run the identical call inside a peak window and it costs double: <strong>$1.50</strong>.
            </p>
            <p>
              <strong className="text-foreground">Example B — heavy reasoning on V4-Pro.</strong> Off-peak, 1M input (cache miss) + 1M output = <strong>$0.66 + $1.98 = $2.64</strong>; in a peak window, <strong>$5.28</strong>. For context: at the pre–31 May 2026 rates the same call cost <strong>$1.74 + $3.48 = $5.22</strong>, and under the flat rates that ran until September 2026 it cost <strong>$1.305</strong> — so off-peak Pro is now roughly twice the mid-2026 flat rate.
            </p>
            <p>
              <strong className="text-foreground">Example C — the ChatGPT comparison.</strong> A task that costs ~$2.64 on V4-Pro or ~$0.75 on V4.1-Flash off-peak would cost several dollars to well over ten dollars on a comparable GPT-class or Claude-class model. Run the numbers for your own volume in the calculator above.
            </p>
            <p>
              <strong className="text-foreground">Example D — a chatbot at scale.</strong> Serving 10M input + 5M output tokens per day on V4.1-Flash at off-peak rates, with a stable system prompt giving an 80% input cache-hit rate: input ≈ 10 × (0.2 × $0.15 + 0.8 × $0.003) = <strong>$0.32/day</strong>; output = 5 × $0.60 = <strong>$3.00/day</strong>. Total ≈ <strong>$3.32/day</strong> — about <strong>$100/month</strong> for 15M tokens daily. The same volume on a comparable US frontier model would run into the hundreds per month.
            </p>
          </div>
        </section>

        {/* Billing */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">How DeepSeek billing works</h2>
          <p className="text-muted-foreground leading-relaxed">
            DeepSeek bills on <strong>tokens</strong>, not on requests or seats. Every API call is charged for its <strong>input tokens</strong> (your prompt, system message and any supplied context) plus its <strong>output tokens</strong> (the model's completion); in thinking mode, reasoning tokens are billed as output. A token is roughly ¾ of an English word — 1M tokens is about 750,000 words. There is no monthly minimum and no per-seat fee: you pay for exactly what you send and receive, deducted post-paid from your prepaid balance.
          </p>
        </section>

        {/* Rate limits */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Rate limits: concurrency only</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            DeepSeek does <strong>not</strong> publish requests-per-minute (RPM) or tokens-per-minute (TPM) limits — neither figure appears anywhere in its documentation. The only documented ceiling is <strong>concurrency</strong>: how many requests you may have in flight at once.
          </p>
          <div className="overflow-x-auto rounded-lg border mb-4">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Table 3 — Documented DeepSeek API concurrency limits</caption>
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Model</th>
                  <th className="text-right py-3 px-4 font-semibold">Concurrent requests</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium"><code className="text-xs bg-muted px-1 rounded">deepseek-v4-pro</code></td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">500</td>
                </tr>
                <tr className="border-t">
                  <td className="py-3 px-4 font-medium"><code className="text-xs bg-muted px-1 rounded">deepseek-v4-flash</code></td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">2,500</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            There are <strong>no account tiers</strong>. Limits do not rise automatically with paid usage or account age — a higher concurrency ceiling requires an <strong>explicit capacity expansion request</strong> to DeepSeek. If you hit a <code className="text-sm bg-muted px-1.5 py-0.5 rounded">429 Too Many Requests</code>, reduce the number of in-flight requests, retry with exponential back-off, and queue rather than fan out. If you need a permanently higher ceiling, contact DeepSeek to request expanded capacity.
          </p>

        </section>

        {/* V4 Flash vs V4 Pro */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">V4.1-Flash vs V4-Pro: which should you use?</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Default to <strong>V4.1-Flash</strong>. It handles the vast majority of production workloads — chat, RAG, extraction, classification, translation, tool-calling — at a fraction of Pro's cost and with the same 1M-token context window. On typical mixed workloads, Flash is roughly 3× cheaper end-to-end than Pro.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Escalate to <strong>V4-Pro</strong> only for calls that measurably fail on Flash: multi-step reasoning, competitive-math or code-golf problems, long-horizon agent planning, and heavy self-debugging coding sessions. A common cost-effective pattern is <strong>hybrid routing</strong> — send every call to Flash first, and re-run on Pro only when a confidence check, evaluation model, or user thumb-down flags the answer as insufficient.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            For a deeper capability breakdown, see the <Link to="/deepseek-v4" className="text-primary hover:underline">DeepSeek V4 model guide</Link>.
          </p>
        </section>

        {/* Cost at scale */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">DeepSeek pricing at scale</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            The gap widens dramatically at production volumes. A team running <strong>100M tokens/day</strong> on V4.1-Flash (roughly 70M input at an 80% cache-hit ratio plus 30M output) pays roughly <strong>$610/month</strong> at off-peak rates. The same token volume on a US frontier API runs into the tens of thousands of dollars per month — run your own numbers against the rate table below rather than trusting a headline multiple.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            The three levers that matter most for cost control at scale: (1) route to V4.1-Flash by default, (2) engineer prompts so the reusable prefix comes first for maximum cache-hit rate, and (3) schedule non-latency-critical batch work outside the two peak windows — that alone halves the rate on every token it moves. Together those three moves typically cut a naive DeepSeek bill by 60–80%.
          </p>
        </section>

        {/* Competitor comparison */}
        <section className="mb-16 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">DeepSeek pricing vs ChatGPT and Claude (2026)</h2>
          <p className="text-muted-foreground mb-6">
            Input / output rates per 1M tokens, verified {COMPETITOR_PRICING.lastVerified}. Competitor rates change frequently — verify each provider's current pricing before committing.
          </p>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4">Model</th>
                  <th className="text-right py-3 px-4">Input</th>
                  <th className="text-right py-3 px-4">Output</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t bg-primary/5">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4.1-Flash <span className="text-xs text-primary ml-1">★ best value</span></td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">$0.14</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">$0.28</td>
                </tr>
                <tr className="border-t bg-primary/5">
                  <td className="py-3 px-4 font-medium">DeepSeek-V4-Pro</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">$0.435</td>
                  <td className="text-right py-3 px-4 text-primary font-semibold">$0.87</td>
                </tr>
                {COMPETITOR_ORDER.map((key) => {
                  const m = COMPETITOR_PRICING.models[key];
                  return (
                    <tr className="border-t" key={key}>
                      <td className="py-3 px-4">
                        {m.label}
                        {m.note && <span className="block text-xs text-muted-foreground">{m.note}</span>}
                      </td>
                      <td className="text-right py-3 px-4">${m.input.toFixed(2)}</td>
                      <td className="text-right py-3 px-4">${m.output.toFixed(2)}</td>
                    </tr>
                  );
                })}

              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            For full feature breakdowns see our{" "}
            <Link to="/deepseek-vs-chatgpt" className="text-primary hover:underline">DeepSeek vs ChatGPT</Link> and{" "}
            <Link to="/deepseek-vs-claude" className="text-primary hover:underline">DeepSeek vs Claude</Link> comparisons.
          </p>
        </section>

        {/* How we keep this accurate */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-primary" />
            How we keep this page accurate
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Pricing pages are only useful when the numbers are current. All rates on this page come from a single rate-config file in our codebase — the table, the calculator and the "last verified" stamp cannot drift out of sync, because they all read the same source. The date shown above is the last time a human editor confirmed the numbers, not the date this page was rebuilt.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Two layers: an automated job compares DeepSeek's <a href={DEEPSEEK_PRICING.source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">official API pricing page</a> against our rate-config every week and flags differences. When it flags something — or on our own review — an editor cross-checks the standing per-token rates, the cache-hit vs cache-miss columns, and the status of the announced peak-hour surcharge, updates the rate-config and the "Last verified" date, and notes the change in the <Link to="/blog" className="text-primary hover:underline">blog changelog</Link>.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            deepseek.ai is not affiliated with DeepSeek. If you spot a stale number, email us via the <Link to="/contact" className="text-primary hover:underline">contact page</Link> — corrections typically ship the same day.
          </p>
        </section>

        {/* Tier cards */}
        <section className="mb-16 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">How to start using DeepSeek</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-primary/20">
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">Free Chat</CardTitle>
                <CardDescription>For personal use</CardDescription>
                <div className="mt-4"><span className="text-4xl font-bold">$0</span><span className="text-muted-foreground">/month</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Unlimited chat access</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>V4.1-Flash & V4-Pro models</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Web search capability</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Chat history saved</span></li>
                </ul>
                <Button className="w-full" asChild>
                  <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackEvent("pricing_cta_click", { tier: "free" }); trackEvent("outbound_deepseek", { source_page: "pricing", tier: "free" }); }}>
                    Start Free <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">Most Popular</div>
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Code className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">API Access</CardTitle>
                <CardDescription>Pay-as-you-go</CardDescription>
                <div className="mt-4"><span className="text-4xl font-bold">$0.15</span><span className="text-muted-foreground">/1M tokens (Flash input, off-peak)</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span><strong>V4.1-Flash:</strong> $0.15 / $0.60 per 1M off-peak</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span><strong>V4-Pro:</strong> $0.66 / $1.98 per 1M off-peak</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Automatic prefix caching</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>OpenAI-compatible endpoint</span></li>
                </ul>
                <Button className="w-full" asChild>
                  <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackEvent("pricing_cta_click", { tier: "api" }); trackEvent("outbound_deepseek", { source_page: "pricing", tier: "api" }); }}>
                    Get API Key <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-muted">
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">Enterprise</CardTitle>
                <CardDescription>For organizations</CardDescription>
                <div className="mt-4"><span className="text-4xl font-bold">Custom</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Volume discounts</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Dedicated support</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Custom SLAs</span></li>
                  <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Priority access</span></li>
                </ul>
                <Button variant="outline" className="w-full" asChild>
                  <a href="https://deepseek.com" target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackEvent("pricing_cta_click", { tier: "enterprise" }); trackEvent("outbound_deepseek", { source_page: "pricing", tier: "enterprise" }); }}>
                    Contact Sales <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently asked questions</h2>
          <div className="space-y-4">
            {FAQ_QA.map((f) => (
              <Card key={f.q}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{f.q}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center bg-muted/50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4">Start Using DeepSeek Today</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Use DeepSeek in any tab with our free Chrome Extension, try the free chat, or grab an API key for your apps.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild className="bg-[#0066FF] hover:bg-[#0066FF]/90">
              <a href={EXTENSION_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => trackEvent("extension_click", { source_page: "pricing", location: "footer_cta" })}>
                <Chrome className="w-5 h-5 mr-2" />
                Add to Chrome — Free
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
                onClick={() => { trackEvent("pricing_cta_click", { tier: "free", location: "footer_cta" }); trackEvent("outbound_deepseek", { source_page: "pricing", tier: "free" }); }}>
                Try Free Chat
              </a>
            </Button>
            <Button size="lg" variant="ghost" asChild>
              <Link to="/deepseek-api" onClick={() => trackEvent("pricing_cta_click", { destination: "/deepseek-api", location: "footer_cta" })}>
                API Setup Guide
              </Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Pricing;
